import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import LotteryBetSideLeft from '../components/LotteryBetSideLeft'
import LotteryBetSideRight from '../components/LotteryBetSideRight'
import type { LotteryRound, PublicHuayType } from '../services/lotteryService'
import { getRoundHuayTypeImage, lotteryService } from '../services/lotteryService'
import { useLotteryRoundCloseGuard } from '@huay/shared/useLotteryRoundCloseGuard'
import { showLotteryRoundClosedAlert } from '../utils/lotteryRoundClosedAlert'
import {
  extractRoundFromRealtimeDetail,
  isLotteryRoundClosed,
  normalizeRoundId,
  resolveRoundIdFromPayload,
} from '@huay/shared/lotteryRoundRealtime'
import { useWallet } from '../contexts/WalletContext'
import { getTenantIdFromConfig } from '../utils/tenantUtils'
import { useTenantThemeConfig } from '../hooks/useTenantThemeConfig'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

function translateLotteryBetError(message?: string, errors?: Array<{ reason?: string; number?: string }>): string {
  const rawMessage = String(message || '').trim()
  const firstError = Array.isArray(errors) ? errors.find((item) => item?.reason || item?.number) : null
  const reason = String(firstError?.reason || '').trim()
  const lowerReason = reason.toLowerCase()
  const lowerMessage = rawMessage.toLowerCase()
  const number = String(firstError?.number || '').trim()

  if (/not available in package|ไม่รองรับในแพ็กเกจ|betting package not found|package not found|ไม่พบแพ็กเกจการเดิมพัน/i.test(rawMessage)) {
    return 'แพ็กเกจนี้ไม่ตรงกับรอบปัจจุบัน กรุณาเลือกแพ็กเกจใหม่'
  }

  if (
    /account_betting_blocked|blocked from betting|กลุ่มจำกัดการแทง|บัญชีนี้ถูกบล็อกการแทง|บัญชีนี้ยังไม่สามารถทำรายการแทงได้/i.test(rawMessage) ||
    /account_betting_blocked|blocked from betting|กลุ่มจำกัดการแทง|บัญชีนี้ถูกบล็อกการแทง|บัญชีนี้ยังไม่สามารถทำรายการแทงได้/i.test(reason)
  ) {
    return 'บัญชีนี้ยังไม่สามารถทำรายการแทงได้ กรุณาติดต่อเจ้าหน้าที่'
  }

  if (
    lowerReason.includes('number is blocked or full') ||
    lowerReason.includes('number has reached its betting limit') ||
    lowerReason.includes('would exceed total betting limit') ||
    lowerMessage.includes('some bet items are no longer valid')
  ) {
    return number
      ? `เลข ${number} ไม่สามารถซื้อได้แล้ว กรุณาลบออกจากรายการแล้วลองใหม่`
      : 'มีบางรายการไม่สามารถซื้อได้แล้ว กรุณาลบเลขที่มีปัญหาออกแล้วลองใหม่'
  }

  if (lowerReason.includes('not active')) return 'รายการนี้ปิดรับแทงแล้ว กรุณาลบออกแล้วลองใหม่'
  if (lowerReason.includes('prize type not found')) return 'ไม่พบประเภทรางวัลของรายการนี้ กรุณาลบออกแล้วลองใหม่'
  if (lowerReason.includes('invalid number length')) return 'รูปแบบเลขไม่ถูกต้อง กรุณาลบรายการนี้แล้วเลือกใหม่'
  if (
    lowerMessage.includes('bad gateway') ||
    lowerMessage.includes('service unavailable') ||
    lowerMessage.includes('gateway timeout') ||
    lowerMessage.includes('not valid json') ||
    lowerMessage.includes('unexpected token <') ||
    lowerMessage.includes('wallet_temporarily_unavailable') ||
    lowerMessage.includes('ระบบกระเป๋าเงินไม่พร้อมใช้งานชั่วคราว')
  ) {
    return 'ระบบรับแทงขัดข้องชั่วคราว กรุณาลองใหม่อีกครั้ง'
  }

  return rawMessage || 'เกิดข้อผิดพลาดในการส่งโพย'
}

interface BettingPackage {
  code: string
  name: string
  packageType: 'RATE_BASED' | 'SET_BASED'
  prizeConfigs?: Array<{
    prizeTypeCode: string
    prizeTypeName: string
    payoutRate: number
    discount: number
    minBet?: number
    maxBet?: number
  }>
  setConfig?: {
    fixedPrice: number
    fixedPayout: number
    discount: number
  }
  [key: string]: any
}

interface BetItem {
  id: string
  number: string
  betType: string
  betTypeLabel: string
  amount: number
  multiplier: number
  discount: number
  payout: number
}

const LotteryBetPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { refreshWallet } = useWallet()
  const { themeConfig } = useTenantThemeConfig()
  const tenantId = getTenantIdFromConfig(themeConfig)
  const initialRound = location.state?.round as LotteryRound | undefined
  const initialPackage = location.state?.package as BettingPackage | undefined
  const navigationRoundId = (location.state as any)?.roundId as string | undefined
  const [round, setRound] = useState<LotteryRound | undefined>(initialRound)
  const [huayTypes, setHuayTypes] = useState<PublicHuayType[]>([])
  const [selectedPackage, setSelectedPackage] = useState<BettingPackage | undefined>(initialPackage)
  const [loadingRound, setLoadingRound] = useState<boolean>(!initialRound)
  const [roundError, setRoundError] = useState<string | null>(null)
  const [selectedBetType, setSelectedBetType] = useState('3 ตัวบน')
  const [numbers, setNumbers] = useState(['', '', '', '', ''])
  const [amount, setAmount] = useState('')
  const [betItems, setBetItems] = useState<BetItem[]>([])
  const [selectedPrizeCodes] = useState<string[]>([])
  const [packageCount, setPackageCount] = useState<number | undefined>(undefined)

  useLotteryRoundCloseGuard({
    roundId: round?._id,
    enabled: Boolean(round?._id),
    onRoundClosed: (roundDisplay) =>
      showLotteryRoundClosedAlert(roundDisplay, 'หน้า /lotto', () => navigate('/lotto', { replace: true })),
    fetchRound: (roundId) => lotteryService.getRoundById(roundId),
    pollIntervalMs: 30_000,
  })

  useEffect(() => {
    if (!round?._id) return

    const handleRoundUpdated = (event: Event) => {
      const updated = extractRoundFromRealtimeDetail((event as CustomEvent).detail)
      if (!updated) return
      if (resolveRoundIdFromPayload(updated) !== normalizeRoundId(round._id)) return
      if (isLotteryRoundClosed(updated)) return
      setRound((prev) => (prev ? { ...prev, ...updated } : prev))
    }

    window.addEventListener('lottery_round_updated', handleRoundUpdated)
    return () => window.removeEventListener('lottery_round_updated', handleRoundUpdated)
  }, [round?._id])

  // Periodic sync while round is still open (yeekee shots, schedule tweaks)
  useEffect(() => {
    if (!round?._id) return

    const syncOpenRound = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      try {
        const currentRound = await lotteryService.getRoundById(round._id)
        if (!currentRound || isLotteryRoundClosed(currentRound)) return
        setRound((prev) => {
          if (!prev) return currentRound
          const statusChanged = prev.status !== currentRound.status
          const yeekeeChanged =
            (prev as { yeekee?: { totalShots?: number } }).yeekee?.totalShots !==
            (currentRound as { yeekee?: { totalShots?: number } }).yeekee?.totalShots
          const timeChanged = prev.betEndTime !== currentRound.betEndTime
          if (!statusChanged && !yeekeeChanged && !timeChanged) return prev
          return { ...prev, ...currentRound }
        })
      } catch (error) {
        console.error('Error checking round status:', error)
      }
    }

    void syncOpenRound()
    const intervalId = window.setInterval(syncOpenRound, 30_000)
    return () => window.clearInterval(intervalId)
  }, [round?._id])

  // Persist the last selected round ID and package for refresh scenarios
  useEffect(() => {
    let cancelled = false
    const loadHuayTypes = async () => {
      try {
        const list = await lotteryService.getTenantHuayTypes({ limit: 100 })
        if (!cancelled) setHuayTypes(list)
      } catch (error) {
        console.error('❌ Error loading huay types for lottery bet page:', error)
      }
    }
    loadHuayTypes()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (initialRound?._id && typeof window !== 'undefined') {
      sessionStorage.setItem('lottery:lastRoundId', initialRound._id)
    }
    if (initialPackage?.code && typeof window !== 'undefined') {
      sessionStorage.setItem('lottery:lastPackageCode', initialPackage.code)
      // Store full package data for reload scenarios
      try {
        sessionStorage.setItem('lottery:lastPackage', JSON.stringify(initialPackage))
      } catch (e) {
        console.warn('Failed to store package in sessionStorage:', e)
      }
    }
  }, [initialRound, initialPackage])

  const derivedRoundId = useMemo(() => {
    if (initialRound?._id) return initialRound._id
    if (navigationRoundId) return navigationRoundId
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('lottery:lastRoundId') || undefined
    }
    return undefined
  }, [initialRound?._id, navigationRoundId])

  // Restore package from sessionStorage on reload
  useEffect(() => {
    if (!selectedPackage && typeof window !== 'undefined') {
      try {
        const storedPackage = sessionStorage.getItem('lottery:lastPackage')
        if (storedPackage) {
          const parsedPackage = JSON.parse(storedPackage) as BettingPackage
          setSelectedPackage(parsedPackage)
          if (process.env.NODE_ENV === 'development') {
            console.log('📦 Restored package from sessionStorage:', parsedPackage.code)
          }
        }
      } catch (e) {
        console.warn('Failed to restore package from sessionStorage:', e)
      }
    }
  }, [selectedPackage])

  useEffect(() => {
    if (!derivedRoundId) {
      // If no round ID found (including after reload), redirect to lotto page
      // This happens when user directly navigates to /lottery-bet without selecting a round
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ No round ID found, redirecting to /lotto')
      }
      navigate('/lotto', { replace: true })
      return
    }

    // Only load round if we don't already have it (to avoid unnecessary API call)
    if (round?._id === derivedRoundId) {
      return
    }

    let cancelled = false

    const loadRound = async () => {
      try {
        setLoadingRound(true)
        const fetchedRound = await lotteryService.getRoundById(derivedRoundId)

        if (cancelled) return

        if (fetchedRound) {
          setRound(fetchedRound)
          setRoundError(null)
          if (typeof window !== 'undefined') {
            sessionStorage.setItem('lottery:lastRoundId', fetchedRound._id)
          }
        } else {
          setRoundError('ไม่พบข้อมูลรอบหวย')
          // Clear invalid round ID from sessionStorage
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem('lottery:lastRoundId')
            sessionStorage.removeItem('lottery:lastPackageCode')
            sessionStorage.removeItem('lottery:lastPackage')
          }
          navigate('/lotto', { replace: true })
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load round detail:', error)
          setRoundError('ไม่สามารถโหลดข้อมูลรอบหวยได้')
          // Clear potentially invalid data from sessionStorage
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem('lottery:lastRoundId')
            sessionStorage.removeItem('lottery:lastPackageCode')
            sessionStorage.removeItem('lottery:lastPackage')
          }
          navigate('/lotto', { replace: true })
        }
      } finally {
        if (!cancelled) {
          setLoadingRound(false)
        }
      }
    }

    loadRound()

    return () => {
      cancelled = true
    }
  }, [derivedRoundId, navigate, round?._id])

  // โหลดจำนวนแพ็กเกจสำหรับปุ่ม "เปลี่ยนแปลงราคาแทง" (disable เมื่อมี package เดียว)
  useEffect(() => {
    if (!round?.huayId) {
      setPackageCount(undefined)
      return
    }
    const accessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    let cancelled = false
    const loadPackages = async () => {
      try {
        const res = await axios.get(
          withApiBaseUrl(`/api/v1/lottery/tenant/${tenantId}/huays/${round.huayId}/packages`),
          { headers }
        )
        if (cancelled) return
        const list = res.data?.data?.packages || []
        setPackageCount(list.length)
      } catch {
        try {
          const res = await axios.get(
            withApiBaseUrl(`/api/v1/lottery/tenant/${tenantId}/betting-packages`),
            { params: { active: 'true' }, headers }
          )
          if (cancelled) return
          const data = res.data?.data || []
          const filtered = data.filter((p: any) => p.isDefault === true)
          setPackageCount((filtered.length > 0 ? filtered : data).length)
        } catch {
          if (!cancelled) setPackageCount(undefined)
        }
      }
    }
    loadPackages()
    return () => { cancelled = true }
  }, [round?.huayId])

  const handleNumberInput = (index: number, value: string) => {
    const newNumbers = [...numbers]
    newNumbers[index] = value
    setNumbers(newNumbers)
  }

  // Generate unique ID for bet items
  const generateBetItemId = () => {
    return `bet_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  // Add bet item
  const addBetItem = useCallback((betData: {
    number: string
    betType: string
    betTypeLabel: string
    amount: number
    multiplier: number
    discount: number
  }) => {
    const id = generateBetItemId()
    const payout = betData.amount * betData.multiplier
    
    const newBetItem: BetItem = {
      id,
      number: betData.number,
      betType: betData.betType,
      betTypeLabel: betData.betTypeLabel,
      amount: betData.amount,
      multiplier: betData.multiplier,
      discount: betData.discount,
      payout: Math.round(payout * 100) / 100 // Round to 2 decimal places
    }

    setBetItems(prev => [...prev, newBetItem])
  }, [])

  // Remove bet item
  const removeBetItem = useCallback((id: string) => {
    setBetItems(prev => prev.filter(item => item.id !== id))
  }, [])

  // Update bet amount
  const updateBetAmount = useCallback((id: string, amount: number) => {
    setBetItems(prev => prev.map(item => {
      if (item.id === id) {
        const payout = amount * item.multiplier
        return {
          ...item,
          amount: amount,
          payout: Math.round(payout * 100) / 100
        }
      }
      return item
    }))
  }, [])

  // Clear all bet items
  const clearAllBetItems = useCallback(() => {
    setBetItems([])
  }, [])

  // Map betType/betTypeLabel (UI) -> prizeTypeCode (backend API)
  const betTypeToPrizeTypeCode = useCallback((betType: string, betTypeLabel?: string): string => {
    if (betTypeLabel === 'ปักหลักร้อย') return 'FIXED_POSITION_HUNDREDS'
    if (betTypeLabel === 'ปักหลักสิบ') return 'FIXED_POSITION_TENS'
    if (betTypeLabel === 'ปักหลักหน่วย') return 'FIXED_POSITION_ONES'
    const mapping: Record<string, string> = {
      top3: '3_DIGIT_TOP',
      down3: '3_DIGIT_BOTTOM',
      tod3: '3_DIGIT_TOD',
      tod3reverse: '3_DIGIT_REVERSE',
      top2: '2_DIGIT_TOP',
      down2: '2_DIGIT_BOTTOM',
      tod2: '2_DIGIT_TOD',
      tod2reverse: '2_DIGIT_REVERSE',
      top1: 'RUNNING_TOP',
      down1: 'RUNNING_BOTTOM',
      top4: '4_DIGIT_TOP',
      tod4: '4_DIGIT_TOD',
      tod5: '5_DIGIT_TOD',
      puk1: 'FIXED_POSITION_ONES',
      puk10: 'FIXED_POSITION_TENS',
      puk100: 'FIXED_POSITION_HUNDREDS'
    }
    return mapping[betType] ?? betType.toUpperCase()
  }, [])

  /** ส่ง API ใช้ 3_DIGIT_TOP / 2_DIGIT_TOP แทน REVERSE (เลขถูกขยายเป็น 6/2 ชุดแล้ว) */
  const prizeTypeCodeForApi = useCallback((prizeTypeCode: string) => {
    if (prizeTypeCode === '3_DIGIT_REVERSE') return '3_DIGIT_TOP'
    if (prizeTypeCode === '2_DIGIT_REVERSE') return '2_DIGIT_TOP'
    return prizeTypeCode
  }, [])
  const [isSubmittingBet, setIsSubmittingBet] = useState(false)

  // Submit bet items
  const submitBetItems = useCallback(async () => {
    if (isSubmittingBet) {
      return
    }
    if (betItems.length === 0) {
      return
    }

    if (!round) {
      toast.error('ไม่พบข้อมูลรอบหวย', { position: 'top-center' })
      return
    }

    try {
      setIsSubmittingBet(true)
      // Get user ID from token or localStorage
      const authToken = localStorage.getItem('accessToken')
      let userId = null
      
      let username: string | undefined
      if (authToken) {
        try {
          const payload = JSON.parse(atob(authToken.split('.')[1]))
          userId = payload.sub || payload.id || payload.userId
          username = payload.username
        } catch (e) {
          console.error('Error parsing token:', e)
        }
      }
      if (!username) {
        try {
          const userStr = localStorage.getItem('user')
          if (userStr) {
            const u = JSON.parse(userStr)
            username = u?.username
          }
        } catch (_) {}
      }

      if (!userId) {
        toast.error('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่', { position: 'top-center' })
        return
      }

      // Call placeBet API (3/2 ตัวกลับ → ส่งเป็น TOP เพราะเลขขยายแล้ว)
      const result = await lotteryService.placeBet(
        userId,
        round._id,
        selectedPackage?.code || null,
        betItems.map(item => {
          const code = betTypeToPrizeTypeCode(item.betType, item.betTypeLabel)
          return {
            prizeTypeCode: prizeTypeCodeForApi(code),
            number: item.number,
            amount: item.amount
          }
        }),
        'web',
        username
      )

      if (result.success) {
        // Clear bet items after successful submission
        clearAllBetItems()
        // อัปเดตยอดเงินใน header หลังหัก wallet
        refreshWallet().catch(() => {})

        const slipId = result.data?.slipId

        // แสดง modal ส่งโพยเรียบร้อย พร้อมปุ่ม ดูโพย / แทงต่อ
        const modalResult = await Swal.fire({
          title: 'ส่งโพยเรียบร้อย',
          text: 'กรุณาตรวจสอบโพยหลังจากที่ส่งทุกครั้ง',
          icon: 'success',
          showCancelButton: true,
          confirmButtonText: 'ดูโพย',
          cancelButtonText: 'แทงต่อ',
          confirmButtonColor: '#28a745',
          cancelButtonColor: '#fd7e14',
          reverseButtons: true
        })

        if (modalResult.isConfirmed && slipId) {
          navigate(`/ticket-detail/${slipId}`)
        }
        // แทงต่อ = ปิด modal อยู่หน้าเดิม (รายการถูกล้างแล้ว)
      } else {
        throw new Error(result.message || 'Failed to place bet')
      }
    } catch (error: any) {
      console.error('Error submitting bet:', error)
      const responseData = error?.response?.data || error?.details
      const translatedMessage = translateLotteryBetError(
        responseData?.message || error?.message,
        responseData?.errors
      )
      toast.error(translatedMessage, {
        position: 'top-center',
        duration: 4000
      })
    } finally {
      setIsSubmittingBet(false)
    }
  }, [betItems, round, selectedPackage, clearAllBetItems, betTypeToPrizeTypeCode, refreshWallet, isSubmittingBet])

  if ((loadingRound && !round) || (!round && roundError)) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="row px--1">
            <div className="col-12 text-center py-5">
              <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
              <p className="c-white mt-2">กำลังโหลดข้อมูลรอบหวย...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!round) {
    return null
  }

  const roundImage = getRoundHuayTypeImage(round, huayTypes) || undefined

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a 
                      href="#" 
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate('/lotto')
                      }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      หน้าหลัก
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="row mx--1">
                      <LotteryBetSideLeft
                        selectedBetType={selectedBetType}
                        setSelectedBetType={setSelectedBetType}
                        numbers={numbers}
                        handleNumberInput={handleNumberInput}
                        addBetItem={addBetItem}
                        round={round}
                        roundImage={roundImage}
                        selectedPackage={selectedPackage}
                        betItems={betItems}
                      />

                      <LotteryBetSideRight
                        amount={amount}
                        setAmount={setAmount}
                        selectedPackage={selectedPackage}
                        round={round}
                        betItems={betItems}
                        removeBetItem={removeBetItem}
                        updateBetAmount={updateBetAmount}
                        clearAllBetItems={clearAllBetItems}
                        selectedPrizeCodes={selectedPrizeCodes}
                        submitBetItems={submitBetItems}
                        isSubmittingBet={isSubmittingBet}
                        packageCount={packageCount}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LotteryBetPage